import React from 'react';
import { Volume2, Loader2 } from 'lucide-react';
import ChatMessage from '../chat/ChatMessage';
import type { ExpertMessage, StreamingMessage, PortfolioStatsData, TradeEvalData, StrategyExtractionData } from '../../types/expert';
import type { UseTTSReturn } from '../../hooks/useTTS';
import { extractSpeechText } from '../../utils/extractSpeechText';
import DisclaimerFooter from './DisclaimerFooter';
import PortfolioStatsCard from './PortfolioStatsCard';
import TradeEvalCard from './TradeEvalCard';
import StrategyCard from './StrategyCard';
import { ToolPill } from './ToolPill';
import AudioEqualizer from './AudioEqualizer';

interface MessageBubbleProps {
  message: ExpertMessage | StreamingMessage;
  tts?: UseTTSReturn;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, tts }) => {
  const isUser = message.role === 'user';
  const isStreaming = 'isStreaming' in message && message.isStreaming;
  const content = message.content || '';
  const messageId = 'id' in message ? (message as ExpertMessage).id : undefined;

  // TTS state — single-message playback
  const isTTSPlaying = tts && messageId != null && tts.currentMessageId === messageId && tts.isPlaying;
  const isTTSLoading = tts && messageId != null && tts.currentMessageId === messageId && tts.isLoading;

  // Queue playback (voice mode) — show equalizer when queue is playing for any streaming message
  const isQueueActive = tts && tts.isQueuePlaying && (tts.isPlaying || tts.isLoading);

  const handleTTSClick = () => {
    if (!tts || messageId == null) return;
    if (isTTSPlaying || isTTSLoading) {
      tts.stop();
    } else {
      const speechText = extractSpeechText(content);
      if (speechText) tts.speak(speechText, messageId);
    }
  };
  const showDisclaimer =
    !isUser &&
    !isStreaming &&
    'metadata' in message &&
    (message as ExpertMessage).metadata?.disclaimer_required === true;

  // Tool pills from streaming message
  const tools = 'tools' in message ? message.tools : undefined;
  // Card data from streaming message
  const cards = 'cards' in message ? message.cards : undefined;

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[80%] px-4 py-3 text-sm ${
          isUser
            ? 'bg-teal-500/10 text-slate-200 rounded-2xl rounded-br-sm'
            : 'bg-slate-800 text-slate-200 rounded-2xl rounded-bl-sm'
        }`}
      >
        {/* Tool execution pills */}
        {tools && tools.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {tools.map((tool, i) => (
              <ToolPill key={`${tool.name}-${i}`} tool={tool} />
            ))}
          </div>
        )}

        {isUser ? (
          <p className="whitespace-pre-wrap">{content}</p>
        ) : (
          <div className="prose prose-invert prose-sm max-w-none prose-p:my-2 prose-p:leading-relaxed prose-headings:my-3 prose-headings:font-semibold prose-ul:my-2 prose-ol:my-2 prose-li:my-1 prose-li:leading-relaxed prose-strong:text-slate-100 prose-strong:font-semibold prose-code:text-teal-300 prose-code:bg-slate-700 prose-code:px-1 prose-code:rounded prose-blockquote:border-teal-500 prose-blockquote:text-slate-300 prose-blockquote:not-italic prose-hr:border-slate-600">
            <ChatMessage content={content} isStreaming={isStreaming} />
            {/* TODO(v3-7-2): sentence highlighting deferred — see AC5.
                 AC5 requires the currently-spoken sentence to be subtly highlighted
                 (e.g. wrapped in <mark> with teal background). This requires surfacing
                 the active sentence index from useTTS to the UI layer and splitting
                 the message content into trackable sentence spans. Deferred to a
                 follow-up story due to complexity of accurately tracking sentence
                 positions in rendered Markdown output. */}
            {/* Audio equalizer during streaming voice playback */}
            {isStreaming && isQueueActive && <AudioEqualizer />}
          </div>
        )}

        {/* Inline card components */}
        {cards && cards.length > 0 && (
          <div className="mt-2 space-y-2">
            {cards.map((card, i) => {
              switch (card.component) {
                case 'portfolio_stats':
                  return <PortfolioStatsCard key={i} data={card.data as unknown as PortfolioStatsData} />;
                case 'trade_evaluation':
                  return <TradeEvalCard key={i} data={card.data as unknown as TradeEvalData} />;
                case 'strategy_extraction':
                  return <StrategyCard key={i} data={card.data as unknown as StrategyExtractionData} />;
                default:
                  return (
                    <div key={i} className="p-2 bg-slate-700/50 rounded-lg text-xs">
                      <span className="text-slate-400">{card.component}</span>
                      <pre className="text-slate-300 mt-1 overflow-x-auto">
                        {JSON.stringify(card.data, null, 2)}
                      </pre>
                    </div>
                  );
              }
            })}
          </div>
        )}

        {/* TTS speaker icon */}
        {!isUser && !isStreaming && tts && messageId != null && content && (
          <div className="flex justify-end mt-1">
            <button
              onClick={handleTTSClick}
              className="p-1 rounded hover:bg-slate-700/50 transition-colors"
              title={isTTSPlaying ? 'Stop speaking' : 'Listen'}
            >
              {isTTSLoading ? (
                <Loader2 size={14} className="text-teal-400 animate-spin" />
              ) : isTTSPlaying ? (
                <Volume2 size={14} className="text-teal-400 animate-pulse" />
              ) : (
                <Volume2 size={14} className="text-slate-500 hover:text-slate-300" />
              )}
            </button>
          </div>
        )}

        {showDisclaimer && <DisclaimerFooter />}
      </div>
    </div>
  );
};

export default MessageBubble;
